import React, { useEffect, useRef, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $getNodeByKey,
  $insertNodes,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { MathNode } from "../dashboard/teacher/lessons/MathNode";
import MathKeyboardModal from "../../../../components/MathEditor/MathKeyboardModal";
import { IconButton } from "@mui/material";
import { Tooltip } from "react-tooltip";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import "./MinimalEditor.css";
import {
  PASTE_COMMAND,
  DROP_COMMAND,
  DRAGSTART_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
} from "lexical";
import { $insertDataTransferForRichText } from "@lexical/clipboard";
import { toast } from "react-toastify";
import useBranding from "../../../../hooks/useBranding";

const theme = {
  paragraph: "editor-paragraph",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
  },
};

/**
 * Serializes the editor's content the way the parent stores it (math nodes as
 * `$$latex$$`). InitialContentPlugin compares against this to tell an echo of
 * our own onChange apart from a genuinely new answer. Call inside a read/update.
 */
function $readEditorText() {
  const root = $getRoot();
  let result = "";

  for (const child of root.getChildren()) {
    const segments = child.getChildren ? child.getChildren() : [];
    for (const segment of segments) {
      if (segment.getType() === "math") {
        result += `$$${segment.getTextContent()}$$`;
      } else {
        result += segment.getTextContent();
      }
    }
  }

  return result;
}

/**
 * Builds a paragraph node from stored answer text (math as `$$latex$$`). Shared by
 * the initial-content load and the anti-tamper hard-restore so both reconstruct
 * content identically. Must be called inside an editor.update().
 */
function $buildParagraphFromContent(content) {
  const paragraph = $createParagraphNode();
  const regex = /\$\$(.+?)\$\$/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    const [fullMatch, latex] = match;
    const index = match.index;
    if (index > lastIndex) {
      paragraph.append($createTextNode(content.slice(lastIndex, index)));
    }
    paragraph.append(new MathNode(latex.trim()));
    paragraph.append($createTextNode(" "));
    lastIndex = index + fullMatch.length;
  }
  if (lastIndex < content.length) {
    paragraph.append($createTextNode(content.slice(lastIndex)));
  }
  return paragraph;
}

function OnChangePlugin({
  onChange,
  parentValue,
  setOldMathNode,
  setShowInputTooltip,
  setLatextextvalue,
  restoredTextRef,
}) {
  const [editor] = useLexicalComposerContext();
  // What the parent already holds, and the last thing we handed it. Kept in refs
  // so the update listener always reads the current values without having to
  // re-register (re-registering would lose nothing, but the listener must not
  // depend on a stale closure either).
  const parentValueRef = useRef(parentValue ?? "");
  const lastSentRef = useRef(null);

  useEffect(() => {
    const next = parentValue ?? "";
    parentValueRef.current = next;
    // The parent moved somewhere we did not put it — a Next/Back navigation, or
    // text arriving from the voice/workboard path. Our "already sent this" memo
    // is about the previous answer, so drop it; otherwise the same string typed
    // on the next question would look like a duplicate and be swallowed.
    if (next !== lastSentRef.current) lastSentRef.current = null;
  }, [parentValue]);
  // removeMathNode();
  const removeMathNode = () => {
    const handleClick = (event) => {
      event.stopPropagation(); // Prevent interference with other click handlers

      const parentSpan = event.target.closest(".math-node");

      if (parentSpan) {
        // Get the corresponding Lexical node key from the DOM element
        const nodeKey = parentSpan.getAttribute("data-lexical-key");
        setOldMathNode(nodeKey); // Store the nodeKey for updates

        if (nodeKey) {
          editor.update(() => {
            const nodeToDelete = $getNodeByKey(nodeKey); // Get the node by key

            if (nodeToDelete) {
              const latexValue = nodeToDelete.getTextContent();

              if (latexValue) {
                setLatextextvalue(latexValue); // Set the LaTeX value
              }
              // Highlight the MathNode visually
              if (parentSpan.className.includes("selected-math-node")) {
                parentSpan.classList.remove("selected-math-node");
                setShowInputTooltip(true); // Hide the tooltip
              } else {
                // Add selection class and show tooltip for editing
                parentSpan.className += " selected-math-node";
                setShowInputTooltip(true);
              }
            }
          });
        }
      }
    };

    // Attach click event listener
    document.addEventListener("click", handleClick);
  };

  removeMathNode();
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const result = $readEditorText();

        // Skip echoes only — the mount commit and InitialContentPlugin's restore
        // both land here with content the parent already has, and re-reporting
        // them would be noise. Counting updates instead ("skip the first one")
        // swallowed the student's first real edit, because Lexical discards the
        // no-op mount update and never spends the count: typing hid that (the
        // next keystroke resends the whole answer) but a single math node dropped
        // in from the Beta keyboard produces exactly one update, so it never
        // reached the parent and never got persisted.
        if (result === parentValueRef.current) return;
        // What InitialContentPlugin just painted from the parent's value. The
        // parse/serialize round trip is not always byte-identical (latex is
        // trimmed), so a restore can land here looking like a student edit.
        // Reporting it back is what leaked an answer across questions: on
        // navigation the editor mounts for one commit with the previous
        // question's answer still in `value`, and echoing that answer made the
        // parent persist it against the *new* step.
        if (result === restoredTextRef?.current) return;
        // Same value again before the parent has re-rendered — nothing new.
        if (result === lastSentRef.current) return;

        // A genuine edit — the restored text is no longer what is on screen.
        if (restoredTextRef) restoredTextRef.current = null;
        lastSentRef.current = result;

        if (onChange) {
          onChange(result);
        }
      });
    });
  }, [editor, onChange]);

  return null;
}

/**
 * Mirrors the parent's `value` into the editor.
 *
 * The parent echoes our own onChange back as `value`, so an insert only happens
 * when `value` differs from what is already on screen. Comparing content — rather
 * than the old "has the student typed in the last 500ms?" timer — means a Next/Back
 * navigation paints the saved answer in the same commit instead of after a timeout,
 * and typing is never interrupted by a re-insert that would move the caret.
 */
function InitialContentPlugin({
  initialContent,
  currentStep,
  restoredTextRef,
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const content = initialContent || "";

    editor.update(
      () => {
        const root = $getRoot();

        // Already showing this exact answer — leave the caret where the student put it.
        if ($readEditorText() === content) return;

        root.clear();

        const paragraph = $createParagraphNode();
        const regex = /\$\$(.+?)\$\$/g;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
          const [fullMatch, latex] = match;
          const index = match.index;

          if (index > lastIndex) {
            paragraph.append($createTextNode(content.slice(lastIndex, index)));
          }

          // Only the math node here — the space that follows a formula is already
          // part of `content` (insertMathNodeSafely stores it). Appending one
          // unconditionally made every restore add another space, so the restored
          // text never matched the parent's value and was reported back as an edit.
          paragraph.append(new MathNode(latex.trim()));

          lastIndex = index + fullMatch.length;
        }

        if (lastIndex < content.length) {
          paragraph.append($createTextNode(content.slice(lastIndex)));
        }

        // A formula at the very end needs a trailing text node, otherwise typing
        // lands inside the math node and corrupts the latex.
        const lastNode = paragraph.getLastChild();
        if (lastNode && lastNode.getType() === "math") {
          paragraph.append($createTextNode(" "));
        }

        root.append(paragraph);

        // Caret at the end of the restored answer so the student can keep writing.
        if (content) {
          paragraph.selectEnd();
        }
        if (restoredTextRef) {
          restoredTextRef.current = $readEditorText();
        }
      },
      { tag: "trusted-insert" },
    );
    // }, [editor, initialContent, currentStep]);
  }, [editor, initialContent, currentStep, restoredTextRef]);

  return null;
}

function Placeholder() {
  return <div className="editor-placeholder">Enter some text...</div>;
}

function InnerEditor({
  onChange,
  onInsertMathLatex,
  isModalOpen,
  closeModal,
  initialContent,
  currentStep,
  setOldMathNode,
  setShowInputTooltip,
  setLatextextvalue,
  latextextvalue,
  showInputTooltip,
  oldMathNode,
  assignmentType,
  branding,
  preventDragDrop,
}) {
  const [editor] = useLexicalComposerContext();
  const savedSelectionRef = React.useRef(null);
  // Serialization of the content InitialContentPlugin last painted from the
  // parent's `value`. Shared with OnChangePlugin so a restore is never mistaken
  // for a student edit and echoed back to the parent.
  const restoredTextRef = React.useRef(null);

  // ── Assessment anti-tamper ──
  // Paste is allowed ONLY for SRDS extended-question editors; everywhere else the
  // field is locked against copy/cut/paste/drop through every channel (keyboard,
  // context menu, drag-drop, and DevTools/Console injection). See guards below.
  const pasteAllowed = assignmentType === "EQ" && branding?.variant === "srds";
  const isComposingRef = React.useRef(false); // IME / mobile autocorrect in progress
  const revertingRef = React.useRef(false); // guards our own revert-update from recursing
  const tamperCountRef = React.useRef(0); // # of injected-content reverts (surface to backend)
  const pasteInProgressRef = React.useRef(false); // a real paste/drop event just fired
  const lastWarnRef = React.useRef(0); // de-dupes the "Pasting disabled" toast

  // Paste fires both PASTE_COMMAND and a `beforeinput` — toast at most once per burst.
  const warnPasteBlocked = React.useCallback(() => {
    const now = Date.now();
    if (now - lastWarnRef.current > 800) {
      lastWarnRef.current = now;
      toast.error("Pasting disabled");
    }
  }, []);

  // A real paste/drop just happened. Flag it briefly so the revert guard removes
  // ANY resulting text (even 1–2 chars that the length heuristic would allow),
  // since a paste event — unlike typing — is unambiguous.
  const markPasteAttempt = React.useCallback(() => {
    pasteInProgressRef.current = true;
    setTimeout(() => {
      pasteInProgressRef.current = false;
    }, 120);
  }, []);

  // Last KNOWN-GOOD answer TEXT (last legit typing / trusted insert), math as
  // `$$latex$$`. We restore from text — not a saved EditorState — because
  // setEditorState to an identical (e.g. empty) model does NOT rewrite the DOM, so
  // foreign text sitting in a Lexical text node would survive. Clearing and
  // rebuilding forces Lexical to own the DOM and wipes the injection.
  const lastGoodTextRef = React.useRef("");

  // Rebuild the field from the last known-good text, re-asserted across a few
  // frames. Covers: DevTools DOM edits that fire no catchable commit, and the
  // "paste then Enter" sequence where the text commits a tick later.
  const forceRestoreGood = React.useCallback(() => {
    const restore = () => {
      revertingRef.current = true;
      try {
        editor.update(
          () => {
            const root = $getRoot();
            root.clear();
            const paragraph = $buildParagraphFromContent(
              lastGoodTextRef.current || "",
            );
            root.append(paragraph);
            if (lastGoodTextRef.current) paragraph.selectEnd();
          },
          { tag: "trusted-insert" },
        );
      } catch (e) {
        // ignore — a later pass or the update guard will catch it
      }
    };
    queueMicrotask(restore);
    setTimeout(restore, 0);
    setTimeout(restore, 130);
  }, [editor]);

  // Save cursor position on every editor update
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          savedSelectionRef.current = {
            anchorKey: selection.anchor.key,
            anchorOffset: selection.anchor.offset,
            focusKey: selection.focus.key,
            focusOffset: selection.focus.offset,
          };
        }
      });
    });
  }, [editor]);

  // Paste handler
  useEffect(() => {
    const unregisterPasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const pasteAllowed =
          assignmentType === "EQ" && branding?.variant === "srds";

        if (!pasteAllowed) {
          event.preventDefault();
          markPasteAttempt(); // flag the burst so the guard removes any-size text
          forceRestoreGood(); // and hard-restore in case it slips past preventDefault
          warnPasteBlocked();
          return true;
        } else {
          try {
            event.preventDefault();
            const clipboardData = event.clipboardData;
            if (!clipboardData) return false;

            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return false;

            if (clipboardData.types.includes("text/html")) {
              $insertDataTransferForRichText(clipboardData, selection);
              return true;
            }

            const text = clipboardData.getData("text/plain");
            if (text) {
              selection.insertText(text);
            }
            return true;
          } catch (error) {
            console.error("Error handling paste:", error);
            return false;
          }
        }
      },
      1,
    );

    return () => {
      unregisterPasteCommand();
    };
  }, [editor, assignmentType]);

  // Block drag-drop into the field (a paste-equivalent vector). Copy/cut are NOT
  // blocked — they're not a cheating vector. Returning true halts Lexical's default.
  useEffect(() => {
    if (pasteAllowed) return;
    const stop = (event) => {
      event?.preventDefault?.();
      return true;
    };
    const unregs = [
      editor.registerCommand(DROP_COMMAND, stop, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        stop,
        COMMAND_PRIORITY_CRITICAL,
      ),
    ];
    return () => unregs.forEach((u) => u());
  }, [editor, pasteAllowed]);

  // DOM-level guards on the editable element: right-click menu, and paste/drop
  // via `beforeinput` (some browsers route these there, bypassing PASTE_COMMAND).
  // Composition flags let the revert guard below leave IME/autocorrect alone.
  useEffect(() => {
    if (pasteAllowed) return;
    let attached = null;
    const onContextMenu = (e) => e.preventDefault();
    const onBeforeInput = (e) => {
      if (
        e.inputType === "insertFromPaste" ||
        e.inputType === "insertFromPasteAsQuotation" ||
        e.inputType === "insertFromDrop"
      ) {
        e.preventDefault();
        markPasteAttempt();
        forceRestoreGood();
        warnPasteBlocked();
      }
    };
    const onCompStart = () => {
      isComposingRef.current = true;
    };
    const onCompEnd = () => {
      isComposingRef.current = false;
    };
    const bind = (root) => {
      if (!root) return;
      root.addEventListener("contextmenu", onContextMenu);
      root.addEventListener("beforeinput", onBeforeInput);
      root.addEventListener("compositionstart", onCompStart);
      root.addEventListener("compositionend", onCompEnd);
      attached = root;
    };
    const unbind = (root) => {
      if (!root) return;
      root.removeEventListener("contextmenu", onContextMenu);
      root.removeEventListener("beforeinput", onBeforeInput);
      root.removeEventListener("compositionstart", onCompStart);
      root.removeEventListener("compositionend", onCompEnd);
    };
    const unregister = editor.registerRootListener((root, prevRoot) => {
      unbind(prevRoot);
      bind(root);
    });
    return () => {
      unbind(attached);
      unregister();
    };
  }, [editor, pasteAllowed]);

  // Backstop for DevTools/Console injection (what the Inspect bypass does): text
  // that appears WITHOUT a real keystroke — a bulk jump in length, not during IME,
  // not one of our trusted programmatic inserts (tagged "trusted-insert"), not an
  // undo/redo ("historic") — is reverted to the previous state. NOTE: a student who
  // deletes this listener in DevTools can still bypass it; the backend
  // keystroke-count-vs-length check (tamperCountRef is ready to submit) is the only
  // guarantee that can't be removed client-side.
  useEffect(() => {
    if (pasteAllowed) return;
    const PASTE_DELTA = 2; // typing adds ~1 char/commit; a jump > 2 = injected/pasted
    return editor.registerUpdateListener(
      ({ editorState, prevEditorState, tags }) => {
        if (revertingRef.current) {
          // Echo of our own restore — don't touch the baseline (it's clean).
          revertingRef.current = false;
          return;
        }

        let curText = "";
        let prevLen = 0;
        editorState.read(() => {
          curText = $readEditorText();
        });
        prevEditorState.read(() => {
          prevLen = $readEditorText().length;
        });

        // Trusted programmatic inserts and undo/redo are legit → new baseline.
        if (
          isComposingRef.current ||
          tags.has("trusted-insert") ||
          tags.has("historic")
        ) {
          lastGoodTextRef.current = curText;
          return;
        }

        const delta = curText.length - prevLen;
        // During a real paste/drop burst, remove ANY added text (even 1–2 chars).
        // Otherwise fall back to the length heuristic that catches DevTools/Console
        // injection while leaving normal typing (~1 char/commit) alone.
        const injected = pasteInProgressRef.current
          ? delta > 0
          : delta > PASTE_DELTA;

        if (injected) {
          // Rebuild from the last KNOWN-GOOD text — clearing+rebuilding forces the
          // DOM to match, so injected text in a Lexical text node is wiped too.
          tamperCountRef.current += 1;
          warnPasteBlocked();
          forceRestoreGood();
          return;
        }

        // Clean edit (normal typing) → advance the baseline.
        lastGoodTextRef.current = curText;
      },
    );
  }, [editor, pasteAllowed]);

  // Last-resort catch for a DIRECT DevTools "Elements" DOM edit: editing a text
  // node there mutates the DOM without a paste event and, in some cases, without a
  // Lexical commit our update-listener can see. We watch the editable element and,
  // when the DOM shows more text than Lexical's model holds (foreign injection),
  // rebuild from the last known-good text. Typing is safe: after Lexical writes the
  // DOM, DOM and model text match, so this only fires on out-of-band edits.
  useEffect(() => {
    if (pasteAllowed) return;
    let el = null;
    let timer = null;
    // Debounced: during typing/IME the DOM can briefly lead the model. We only act
    // if the DOM is STILL ahead ~70ms later, by which point Lexical has synced —
    // so a legit keystroke is never wiped, only a persistent foreign edit.
    const schedule = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (revertingRef.current || isComposingRef.current) return;
        const node = el;
        if (!node) return;
        let modelText = "";
        editor.getEditorState().read(() => {
          modelText = $readEditorText();
        });
        // Math nodes render to DOM text that doesn't match their `$$latex$$` model
        // form, so this length comparison is unreliable when math is present — skip
        // it. (Paste is still blocked by the event + delta guards.) This also stops
        // a feedback loop where a math insert kept re-tripping this watcher and
        // spamming "Pasting disabled" toasts.
        if (modelText.includes("$$")) return;
        const domLen = (node.textContent || "").length;
        // DOM longer than the model means foreign text was injected out of band.
        if (domLen > modelText.length + 1) {
          tamperCountRef.current += 1;
          warnPasteBlocked();
          forceRestoreGood();
        }
      }, 70);
    };
    const observer = new MutationObserver(schedule);
    const unregisterRoot = editor.registerRootListener((root, prevRoot) => {
      if (prevRoot) observer.disconnect();
      if (root) {
        el = root;
        observer.observe(root, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    });
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
      unregisterRoot();
    };
  }, [editor, pasteAllowed]);

  // Helper: inserts mathNode safely at saved or fallback position
  const insertMathNodeSafely = (strippedLatex) => {
    editor.update(
      () => {
        const mathNode = new MathNode(strippedLatex);
        const spaceNode = $createTextNode(" ");

        // Try restoring saved selection
        if (savedSelectionRef.current) {
          const { anchorKey } = savedSelectionRef.current;
          const anchorNode = $getNodeByKey(anchorKey);

          if (anchorNode) {
            const parentParagraph = anchorNode.getParent();

            // Safety check: parent must be a paragraph (not root)
            if (parentParagraph && parentParagraph.getType() === "paragraph") {
              anchorNode.insertAfter(spaceNode);
              anchorNode.insertAfter(mathNode);
              spaceNode.select();
              return;
            }
          }
        }

        // Fallback: append to last paragraph in root
        const root = $getRoot();
        const lastChild = root.getLastChild();
        if (lastChild && lastChild.getType() === "paragraph") {
          lastChild.append(mathNode);
          lastChild.append(spaceNode);
          spaceNode.select();
        } else {
          // Ultimate fallback: create a new paragraph
          const paragraph = $createParagraphNode();
          paragraph.append(mathNode);
          paragraph.append(spaceNode);
          root.append(paragraph);
          spaceNode.select();
        }
      },
      { tag: "trusted-insert" },
    );
  };

  const handleInsertMath = (latex) => {
    if (!latex) return;

    let normalizedLatex = latex
      .replace(/\\"/g, "\\ddot{a}")
      .replace(/\\'/g, "\\acute{a}")
      .replace(/\\\//g, "\\slash")
      .replace(/\\=/g, "\\overline{a}")
      .replace(/\\Colon/g, "\\mathbin{:}")
      .replace(/\\Laplace/g, "\\mathscr{L}")
      .replace(/\\Lbag/g, "\\langle")
      .replace(/\\Longmapsfrom/g, "\\longleftarrow")
      .replace(/\\Longmapsto/g, "\\longrightarrow")
      .replace(/\\MapsTo/g, "\\rightarrow")
      .replace(/\\Mapsfrom/g, "\\leftarrow")
      .replace(/\\Rbag/g, "\\rangle")
      .replace(/\\Yup/g, "\\checkmark")
      .replace(/\\^/g, "\\hat{a}")
      .replace(/\\`/g, "\\grave{a}")
      .replace(/\\backdoubleprime/g, "\\prime\\prime")
      .replace(/\\biconditional/g, "\\leftrightarrow")
      .replace(/\\biginterleave/g, "\\parallel")
      .replace(/\\boxast/g, "\\square\\mkern-5mu\\ast")
      .replace(/\\boxbar/g, "\\square\\mkern-5mu\\vert")
      .replace(/\\boxbox/g, "\\square\\square")
      .replace(/\\boxbslash/g, "\\square\\mkern-5mu\\backslash")
      .replace(/\\boxcircle/g, "\\square\\mkern-5mu\\circ")
      .replace(/\\boxslash/g, "\\square\\mkern-5mu/")
      .replace(/\\capitalDifferentialD/g, "\\mathrm{D}")
      .replace(/\\differencedelta/g, "\\Delta")
      .replace(/\\differentialD/g, "\\mathrm{d}")
      .replace(/\\divides/g, "\\mid")
      .replace(/\\doubleStruckCapitalN/g, "\\mathbb{N}")
      .replace(/\\doubleStruckCapitalP/g, "\\mathbb{P}")
      .replace(/\\doubleStruckCapitalQ/g, "\\mathbb{Q}")
      .replace(/\\doubleStruckCapitalR/g, "\\mathbb{R}")
      .replace(/\\doubleStruckCapitalZ/g, "\\mathbb{Z}")
      .replace(/\\doubleprime/g, "\\prime\\prime")
      .replace(/\\enskip/g, "\\;")
      .replace(/\\eqdef/g, "\\triangleq")
      .replace(/\\error/g, "Error")
      .replace(/\\exponentialE/g, "\\mathrm{e}")
      .replace(/\\fatsemi/g, "\\mathbin{;}")
      .replace(/\\fontfamily/g, "\\mathrm{text}")
      .replace(/\\fontseries/g, "\\mathbf{text}")
      .replace(/\\fontshape/g, "\\mathit{text}")
      .replace(/\\gothicCapitalC/g, "\\mathfrak{C}")
      .replace(/\\gothicCapitalH/g, "\\mathfrak{H}")
      .replace(/\\gothicCapitalI/g, "\\mathfrak{I}")
      .replace(/\\gothicCapitalR/g, "\\mathfrak{R}")
      .replace(/\\hspace\*/g, "\\hspace{1em}")
      .replace(/\\imaginaryI/g, "\\mathrm{i}")
      .replace(/\\imaginaryJ/g, "\\mathrm{j}")
      .replace(/\\intclockwise/g, "\\oint")
      .replace(/\\intctrclockwise/g, "\\oint")
      .replace(/\\interleave/g, "\\parallel")
      .replace(/\\laplace/g, "\\mathcal{L}")
      .replace(/\\lb\b/g, "\\lbrace")
      .replace(/\\leftarrowtriangle/g, "\\triangleleft\\mkern-2mu\\leftarrow")
      .replace(
        /\\leftrightarrowtriangle/g,
        "\\triangleleft\\mkern-2mu\\leftrightarrow\\mkern-2mu\\triangleright",
      )
      .replace(/\\leftslice/g, "/")
      .replace(/\\lightning/g, "\\triangle")
      .replace(/\\longmapsfrom/g, "\\longleftarrow")
      .replace(/\\mVert/g, "\\lVert\\rVert")
      .replace(/\\mapsfrom/g, "\\leftarrow")
      .replace(/\\mathbfit/g, "\\mathbf{\\mathit{x}}")
      .replace(/\\measeq/g, "\\cong")
      .replace(/\\mvert/g, "\\lvert\\rvert")
      .replace(/\\nicefrac/g, "\\frac{a}{b}")
      .replace(/\\nor/g, "\\downarrow")
      .replace(/\\nsubset/g, "\\not\\subset")
      .replace(/\\nsupset/g, "\\not\\supset")
      .replace(/\\ointctrclockwise/g, "\\oint")
      .replace(/\\ordinarycolon/g, "\\colon")
      .replace(/\\originalof/g, "\\cong")
      .replace(/\\overarc/g, "\\overparen{x}")
      .replace(/\\overunderset/g, "\\overset{a}{\\underset{b}{c}}")
      .replace(/\\pdiff/g, "\\partial")
      .replace(/\\questeq/g, "\\overset{?}{=}")
      .replace(/\\rD/g, "\\mathrm{D}")
      .replace(/\\rd/g, "\\mathrm{d}")
      .replace(
        /\\rightarrowtriangle/g,
        "\\rightarrow\\mkern-2mu\\triangleright",
      )
      .replace(/\\rightslice/g, "\\backslash")
      .replace(/\\rmfamily/g, "\\mathrm{text}")
      .replace(/\\roundimplies/g, "\\Rightarrow")
      .replace(/\\scriptCapitalE/g, "\\mathcal{E}")
      .replace(/\\scriptCapitalH/g, "\\mathcal{H}")
      .replace(/\\scriptCapitalL/g, "\\mathcal{L}")
      .replace(/\\scshape/g, "\\text{\\scshape text}")
      .replace(/\\selectfont/g, "")
      .replace(/\\sffamily/g, "\\mathsf{text}")
      .replace(/\\slshape/g, "\\mathit{text}")
      .replace(/\\sslash/g, "\\slash\\slash")
      .replace(/\\stackbin/g, "\\overset{a}{b}")
      .replace(/\\talloblong/g, "\\parallel")
      .replace(/\\ttfamily/g, "\\mathtt{text}")
      .replace(/\\underarc/g, "\\underparen{x}")
      .replace(/\\varointclockwise/g, "\\oint")
      .replace(/\\placeholder/g, "\\Box")
      .replace(/\\~/g, "\\tilde{a}")
      .replace(/\\bigm\{\|\}/g, "\\mid")
      .replace(/\\bigm\{\|}/g, "\\mid")
      .replace(/\\bigm\|/g, "\\mid")
      .replace(/\\displaylines/g, "\\;");

    const strippedLatex = normalizedLatex.replace(/^\$\$|\$\$$/g, "");

    if (latextextvalue === "" || latextextvalue === null) {
      // ── INSERT NEW MATH NODE ──
      insertMathNodeSafely(strippedLatex);
      onInsertMathLatex(strippedLatex);
    } else {
      // ── EDIT EXISTING MATH NODE ──

      if (strippedLatex === "delete") {
        if (oldMathNode) {
          editor.update(() => {
            const nodeToDelete = $getNodeByKey(oldMathNode);
            if (nodeToDelete) nodeToDelete.remove();
          });
        }
        setShowInputTooltip(false);
        setLatextextvalue(null);
        return;
      }

      // Remove old node first, save its sibling as anchor, then insert updated one
      editor.update(() => {
        if (oldMathNode) {
          const nodeToDelete = $getNodeByKey(oldMathNode);
          if (nodeToDelete) {
            const prevSibling = nodeToDelete.getPreviousSibling();
            const nextSibling = nodeToDelete.getNextSibling();
            const parent = nodeToDelete.getParent();

            // Save a nearby node as the new anchor for insertion
            if (prevSibling) {
              savedSelectionRef.current = {
                anchorKey: prevSibling.getKey(),
                anchorOffset: 0,
                focusKey: prevSibling.getKey(),
                focusOffset: 0,
              };
            } else if (nextSibling) {
              savedSelectionRef.current = {
                anchorKey: nextSibling.getKey(),
                anchorOffset: 0,
                focusKey: nextSibling.getKey(),
                focusOffset: 0,
              };
            } else if (parent) {
              // No siblings — use parent paragraph directly
              savedSelectionRef.current = {
                anchorKey: parent.getKey(),
                anchorOffset: 0,
                focusKey: parent.getKey(),
                focusOffset: 0,
              };
            }

            nodeToDelete.remove();
          }
        }
      });

      // Insert replacement after removal is complete
      setTimeout(() => {
        insertMathNodeSafely(strippedLatex);
        setShowInputTooltip(false);
        setLatextextvalue("");
      }, 0);
    }
  };

  // Build capture-phase drag handlers to intercept events BEFORE Lexical's
  // native listeners on ContentEditable process them. Bubble-phase handlers
  // on a parent fire too late — Lexical has already handled the drop by then.
  const captureHandlers = preventDragDrop
    ? {
        onDragEnterCapture: preventDragDrop,
        onDragOverCapture: preventDragDrop,
        onDragLeaveCapture: preventDragDrop,
        onDropCapture: preventDragDrop,
      }
    : {};

  return (
    <>
      <div className="editor-inner" {...captureHandlers}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="editor-input" />}
          placeholder={<Placeholder />}
        />
        <OnChangePlugin
          onChange={onChange}
          parentValue={initialContent}
          restoredTextRef={restoredTextRef}
          setOldMathNode={setOldMathNode}
          setShowInputTooltip={setShowInputTooltip}
          setLatextextvalue={setLatextextvalue}
        />
        <InitialContentPlugin
          initialContent={initialContent}
          currentStep={currentStep}
          restoredTextRef={restoredTextRef}
        />
      </div>

      <MathKeyboardModal
        isOpen={isModalOpen || showInputTooltip}
        onClose={closeModal}
        onInsert={handleInsertMath}
        latextextvalue={latextextvalue}
        assignmentType={assignmentType}
      />
    </>
  );
}

const MinimalEditor = ({
  preventDragDrop = null,
  value,
  onChange = () => {},
  placeholder = "Enter some text...",
  assignment_id,
  currentStep,
  assignmentType,
  //bb
  onMathKeyboardToggle = () => {}, // NEW
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [oldMathNode, setOldMathNode] = useState(null);
  const [showInputTooltip, setShowInputTooltip] = useState(false);
  const [latextextvalue, setLatextextvalue] = useState(null);
  const branding = useBranding();

  const openKeyboard = () => {
    setIsModalOpen(true);
  };

  const closeKeyboard = () => {
    setIsModalOpen(false);
    setShowInputTooltip(false);
    setLatextextvalue(null);
  };
  const handleChange = (textContent) => {
    const mockEvent = {
      target: {
        value: textContent,
      },
    };
    onChange(mockEvent);
  };
  const editorConfig = {
    namespace: "MinimalEditor",
    theme,
    onError: (error) => console.error(error),
    nodes: [MathNode],
  };

  //bb
  const isKeyboardOpen = isModalOpen || showInputTooltip;

  useEffect(() => {
    onMathKeyboardToggle(isKeyboardOpen);
    // Safety: ensure the flag resets if this editor unmounts while open
    // (e.g. student navigates to an MCQ question)
    return () => onMathKeyboardToggle(false);
  }, [isKeyboardOpen]);

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="minimal-editor-wrapper">
        <div className="editor-container">
          <InnerEditor
            onChange={handleChange}
            onInsertMathLatex={(latex) => {}}
            isModalOpen={isModalOpen}
            closeModal={closeKeyboard}
            initialContent={value}
            currentStep={currentStep}
            setOldMathNode={setOldMathNode}
            setShowInputTooltip={setShowInputTooltip}
            setLatextextvalue={setLatextextvalue}
            showInputTooltip={showInputTooltip}
            latextextvalue={latextextvalue}
            oldMathNode={oldMathNode}
            assignmentType={assignmentType}
            branding={branding}
            preventDragDrop={preventDragDrop}
          />

          <div
            className="editor-toolbar"
            style={{
              position: "sticky",
              top: "10px",
              float: "right",
              marginTop: "-140px",
              marginRight: "20px",
              // zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            <IconButton
              onClick={openKeyboard}
              aria-label="open math keyboard"
              data-tooltip-id="math-tooltip3"
            >
              <KeyboardIcon style={{ color: "#5094E7" }} />
              <p style={{ fontSize: "11px", color: "#5094E7" }}>Beta</p>
            </IconButton>
            <Tooltip
              id="math-tooltip3"
              place="top"
              content="Beta version: Results may not be as expected."
              effect="solid"
              type="light"
            />
          </div>
        </div>
      </div>
    </LexicalComposer>
  );
};

export default MinimalEditor;
